<template>
  <div>
    <div class="text-right">Close calendar to save changes to <strong>{{ data.name }}'s</strong> days off</div>
    <div class="text-center">
      <v-calendar
        class="custom-calendar max-w-full"
        :masks="masks"
        :attributes="attributes"
        disable-page-swipe
        is-expanded
        @update:to-page="changePage"
        :min-date="new Date()"
        :firstDayOfWeek="1"
        :disabled-dates="{ weekdays: [1, 7] }"
      >
        <template v-slot:day-content="{ day, attributes }">
          <div style="min-height: 90px;" class="flex flex-col h-full z-10 overflow-hidden" @click="onDayClick(day)">
            <span class="day-label text-sm text-gray-900">{{ day.day }}</span>
            <div class="flex-grow overflow-y-auto overflow-x-auto" style="padding-top: 10px;">
              <p
                v-for="attr in attributes"
                :key="attr.key"
                class="text-xs leading-tight rounded-sm p-1 mt-0 mb-1"
                :class="attr.customData.class"
              >
                {{ attr.customData.title }}
              </p>
            </div>
          </div>
        </template>
      </v-calendar>
    </div>
  </div>
</template>

<script>
import {
  clearLocalStorage,
  daysOffMergePatchPayload,
  notifyError,
  notifySuccess,
  objectBy
} from 'src/utilities/utils'
import _ from 'lodash'
import {NOT_FOUND, OK} from 'http-status-codes'
import Constants from 'src/utilities/constants'

export default {
  props: ['data'],
  data() {
    return {
      masks: {
        weekdays: 'WWW',
      },
      attributes: [],
      currentPage: {
        month: new Date().getMonth(),
        year: new Date().getFullYear()
      },
      daysOffChanged: false
    }
  },
  methods: {
    changePage(page) {
      this.currentPage = page
    },
    onDayClick(calendarDay) {
      this.daysOffChanged = true
      const key = `${calendarDay.year}-${calendarDay.month}-${calendarDay.day}`
      const selectedDay = objectBy(this.attributes, 'key', key)

      if (selectedDay) { // if day is already selected, de-select it
        this.$store.commit('users/user/removeDayOff', calendarDay.date)
        this.attributes = _.filter(this.attributes, (attribute) => attribute.key !== selectedDay.key)
      } else {
        this.$store.commit('users/user/addDayOff', calendarDay.date)
        this.attributes.push(
          {
            key: `${this.currentPage.year}-${this.currentPage.month}-${calendarDay.day}`,
            customData: {
              title: "AWAY",
              class: 'bg-pink-500 text-center text-black'
            },
            dates: calendarDay.date,
          }
        )
      }
    },
    clearCacheLocalStorage() {
      // purge the users cache and local storage after a user has been edited
      if (this.$store.cache.has('users/fetchAll')) {
        this.$store.cache.delete('users/fetchAll')
      }
      clearLocalStorage(Constants.LOCAL_STORAGE.LOCATION.USER_DATA)
      clearLocalStorage(Constants.LOCAL_STORAGE.LOCATION.USER_MAP_DATA)
      this.$store.set('users/allUserPreferencesLoaded', false)
    }
  },
  async mounted() {
    const response = await this.$store.dispatch('fetchById', {
      endpoint: Constants.ENTITY_ENDPOINTS.USERS,
      id: this.data.id
    })

    if (OK !== response.status) {
      notifyError(this, 'Failed to load user with ID: ' + this.data.id)
      return
    }

    const daysOff = response.data.daysOff || []
    const daysOffDates = []
    // array of dates in the user.daysOff property coming from API
    // are strings for some reason, convert to Date objects
    daysOff.forEach(dayOff => daysOffDates.push(new Date(dayOff)))

    this.$store.set('users/user@daysOff', daysOffDates)

    if (daysOffDates.length > 0) {
      for (const dayOff of daysOffDates) {
        const key = `${dayOff.getFullYear()}-${dayOff.getMonth() + 1}-${dayOff.getDate()}`
        this.attributes.push(
          {
            key: key,
            customData: {
              title: "AWAY",
              class: 'bg-pink-500 text-center text-black'
            },
            dates: dayOff
          }
        )
      }
    }
  },
  async destroyed() {
    if (this.daysOffChanged) {
      const user = this.$store.get('users@user')
      user.id = this.data.id

      const today = new Date();
      const startOfToday = new Date(`${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`)
      // prune any dates before today to keep the user's daysOff property containing only future dates
      user.daysOff = user.daysOff.filter(dayOff => dayOff.getTime() >= startOfToday)

      const payload = daysOffMergePatchPayload(user)
      const response = await this.$store.dispatch('mergePatch2', payload)

      if (OK === response.status) {
        this.clearCacheLocalStorage()
        notifySuccess(this, 'Calendar changes saved successfully')
        this.$store.commit('users/updateUserResultDaysOff', response.data)
        return
      }

      notifyError(this, 'Failed to save calendar changes')
    }
  }
}
</script>

<style lang="postcss" scoped>
::-webkit-scrollbar {
  width: 0;
}

::-webkit-scrollbar-track {
  display: none;
}

/deep/ .custom-calendar.vc-container {
  --day-border: 1px solid #b8c2cc;
  --day-border-highlight: 1px solid #b8c2cc;
  --day-width: 90px;
  --day-height: 90px;
  --weekday-bg: #f8fafc;
  --weekday-border: 1px solid #eaeaea;

  border-radius: 0;
  width: 100%;

& .vc-header {
    background-color: #f1f5f8;
    padding: 10px 0;
  }
& .vc-weeks {
    padding: 0;
  }
& .vc-weekday {
    background-color: var(--weekday-bg);
    border-bottom: var(--weekday-border);
    border-top: var(--weekday-border);
    padding: 5px 0;
  }
& .vc-day {
    padding: 0 5px 3px 5px;
    text-align: left;
    height: var(--day-height);
    min-width: var(--day-width);
    background-color: white;
&.weekday-1,
&.weekday-7 {
   background-color: #eff8ff;
 }
&:not(.on-bottom) {
   border-bottom: var(--day-border);
&.weekday-1 {
   border-bottom: var(--day-border-highlight);
 }
}
&:not(.on-right) {
   border-right: var(--day-border);
 }
}
& .vc-day-dots {
    margin-bottom: 5px;
  }
}
</style>
